import {
  PartRelationshipEdge,
  PartRelationshipEdgeType,
  PartRelationshipGraph,
  PartRelationshipNode,
  PartRelationshipNodeType,
} from '@/api/generatedApi';

export type RelationshipFilter =
  | 'all'
  | 'reference-numbers'
  | 'supersession'
  | 'shared-references'
  | 'interchangeable'
  | 'made-from'
  | 'end-items'
  | 'suppliers'
  | 'contract-awards';

export const RELATIONSHIP_FILTERS: {
  value: RelationshipFilter;
  label: string;
  edgeTypes: PartRelationshipEdgeType[];
}[] = [
  { value: 'all', label: 'All', edgeTypes: Object.values(PartRelationshipEdgeType) },
  {
    value: 'reference-numbers',
    label: 'Part numbers & CAGEs',
    edgeTypes: [PartRelationshipEdgeType.HAS_REFERENCE_NUMBER, PartRelationshipEdgeType.REFERENCE_LISTED_BY],
  },
  { value: 'supersession', label: 'Replacements', edgeTypes: [PartRelationshipEdgeType.SUPERSEDED_BY] },
  {
    value: 'shared-references',
    label: 'Same part number and CAGE',
    edgeTypes: [PartRelationshipEdgeType.SHARES_REFERENCE_NUMBER],
  },
  {
    value: 'interchangeable',
    label: 'Interchangeable & substitutes',
    edgeTypes: [PartRelationshipEdgeType.INTERCHANGEABLE_WITH, PartRelationshipEdgeType.SUBSTITUTE_FOR],
  },
  { value: 'made-from', label: 'Made from', edgeTypes: [PartRelationshipEdgeType.FABRICATED_FROM] },
  {
    value: 'end-items',
    label: "Where it's used",
    edgeTypes: [PartRelationshipEdgeType.LABELED_FOR_END_ITEM, PartRelationshipEdgeType.COMPONENT_OF],
  },
  { value: 'suppliers', label: 'Suppliers', edgeTypes: [PartRelationshipEdgeType.SUPPLIED_BY] },
  {
    value: 'contract-awards',
    label: 'Contract awards',
    edgeTypes: [PartRelationshipEdgeType.HAS_ACQUISITION_RECORD],
  },
];

export const EDGE_LABELS: Record<PartRelationshipEdgeType, string> = {
  [PartRelationshipEdgeType.HAS_REFERENCE_NUMBER]: 'has part number',
  [PartRelationshipEdgeType.REFERENCE_LISTED_BY]: 'listed by',
  [PartRelationshipEdgeType.SHARES_REFERENCE_NUMBER]: 'same part number and CAGE',
  [PartRelationshipEdgeType.SUPERSEDED_BY]: 'replaced by',
  [PartRelationshipEdgeType.INTERCHANGEABLE_WITH]: 'interchangeable with',
  [PartRelationshipEdgeType.SUBSTITUTE_FOR]: 'substitute for',
  [PartRelationshipEdgeType.FABRICATED_FROM]: 'made from',
  [PartRelationshipEdgeType.LABELED_FOR_END_ITEM]: 'goes into',
  [PartRelationshipEdgeType.COMPONENT_OF]: 'component of',
  [PartRelationshipEdgeType.SUPPLIED_BY]: 'supplied by',
  [PartRelationshipEdgeType.HAS_ACQUISITION_RECORD]: 'bought under',
};

const CATALOG_IDENTITY_EDGES = new Set([
  PartRelationshipEdgeType.HAS_REFERENCE_NUMBER,
  PartRelationshipEdgeType.REFERENCE_LISTED_BY,
]);

const COLUMN_WIDTH = 300;
const ROW_HEIGHT = 76;
const MAX_ROWS_PER_LANE = 12;

export interface PositionedNode {
  node: PartRelationshipNode;
  column: number;
  x: number;
  y: number;
}

export interface VisibleGraph {
  nodes: PartRelationshipNode[];
  edges: PartRelationshipEdge[];
}

const keepNodesOnEdges = (
  nodes: PartRelationshipNode[],
  edges: PartRelationshipEdge[],
  anchorId: string
): VisibleGraph => {
  const visible = new Set([anchorId, ...edges.flatMap(edge => [edge.sourceNodeId, edge.targetNodeId])]);
  return { nodes: nodes.filter(node => visible.has(node.id)), edges };
};

export const filterRelationshipGraph = (graph: PartRelationshipGraph, filter: RelationshipFilter): VisibleGraph => {
  const edgeTypes = new Set(RELATIONSHIP_FILTERS.find(option => option.value === filter)?.edgeTypes);
  return keepNodesOnEdges(
    graph.nodes,
    graph.edges.filter(edge => edgeTypes.has(edge.type)),
    graph.seedNodeId
  );
};

export const focusOnNode = (visible: VisibleGraph, nodeId: string | undefined): VisibleGraph => {
  if (!nodeId) return visible;
  return keepNodesOnEdges(
    visible.nodes,
    visible.edges.filter(edge => edge.sourceNodeId === nodeId || edge.targetNodeId === nodeId),
    nodeId
  );
};

const SHARED_REFERENCE_CAPTION = 'Same part no. & CAGE';
const INTERCHANGEABLE_CAPTION = 'Interchangeable';

const RELATION_CAPTIONS: Partial<Record<string, string>> = {
  [`${PartRelationshipEdgeType.SUPERSEDED_BY}:into-seed`]: 'Replaced by this part',
  [`${PartRelationshipEdgeType.SUPERSEDED_BY}:from-seed`]: 'Replaces this part',
  [`${PartRelationshipEdgeType.SHARES_REFERENCE_NUMBER}:into-seed`]: SHARED_REFERENCE_CAPTION,
  [`${PartRelationshipEdgeType.SHARES_REFERENCE_NUMBER}:from-seed`]: SHARED_REFERENCE_CAPTION,
  [`${PartRelationshipEdgeType.INTERCHANGEABLE_WITH}:into-seed`]: INTERCHANGEABLE_CAPTION,
  [`${PartRelationshipEdgeType.INTERCHANGEABLE_WITH}:from-seed`]: INTERCHANGEABLE_CAPTION,
  [`${PartRelationshipEdgeType.SUBSTITUTE_FOR}:into-seed`]: 'Substitute for this part',
  [`${PartRelationshipEdgeType.SUBSTITUTE_FOR}:from-seed`]: 'This part substitutes',
  [`${PartRelationshipEdgeType.FABRICATED_FROM}:into-seed`]: 'Made from this part',
  [`${PartRelationshipEdgeType.FABRICATED_FROM}:from-seed`]: 'Used to make this part',
  [`${PartRelationshipEdgeType.LABELED_FOR_END_ITEM}:into-seed`]: 'Goes into this part',
  [`${PartRelationshipEdgeType.LABELED_FOR_END_ITEM}:from-seed`]: 'Goes into (end item)',
  [`${PartRelationshipEdgeType.COMPONENT_OF}:into-seed`]: 'Component of this part',
  [`${PartRelationshipEdgeType.COMPONENT_OF}:from-seed`]: 'End item of this part',
};

export const relationCaptions = (edges: PartRelationshipEdge[], seedNodeId: string): Map<string, string> => {
  const captions = new Map<string, string>();
  for (const edge of edges) {
    const intoSeed = edge.targetNodeId === seedNodeId;
    if (!intoSeed && edge.sourceNodeId !== seedNodeId) continue;
    const neighborId = intoSeed ? edge.sourceNodeId : edge.targetNodeId;
    const caption = RELATION_CAPTIONS[`${edge.type}:${intoSeed ? 'into-seed' : 'from-seed'}`];
    if (caption && !captions.has(neighborId)) captions.set(neighborId, caption);
  }
  return captions;
};

export const hasNeighborsBeyondCatalogIdentity = (graph: PartRelationshipGraph): boolean =>
  graph.edges.some(edge => !CATALOG_IDENTITY_EDGES.has(edge.type));

export const describeEvidence = ({ evidence }: PartRelationshipEdge): string => {
  const summary = `${evidence.source} · ${evidence.kind} · ${Number(evidence.confidence.toFixed(2))}`;
  return evidence.matchedOn?.length ? `${summary} · matched on ${evidence.matchedOn.join(', ')}` : summary;
};

export const describeGraphSize = (graph: PartRelationshipGraph): string => {
  const size = `${graph.nodes.length} nodes / ${graph.edges.length} edges (cap ${graph.caps.maxNodes} / ${graph.caps.maxEdges})`;
  return graph.truncated ? `${size} · more relationships exist than shown` : size;
};

export const describeComponents = ({ components }: PartRelationshipGraph): string | undefined => {
  if (components.total === 0) return undefined;
  const count = `${components.total.toLocaleString('en-US')} ${components.total === 1 ? 'component' : 'components'}`;
  return components.shown < components.total ? `${count}, showing ${components.shown}` : count;
};

const columnOf = (node: PartRelationshipNode, seedNodeId: string): number => {
  if (node.id === seedNodeId) return 1;
  switch (node.type) {
    case PartRelationshipNodeType.Nsn: {
      return 0;
    }
    case PartRelationshipNodeType.PartNumber: {
      return 2;
    }
    case PartRelationshipNodeType.Cage: {
      return 3;
    }
    default: {
      return 4;
    }
  }
};

const byTypeThenLabel = (a: PartRelationshipNode, b: PartRelationshipNode): number =>
  a.type.localeCompare(b.type) || a.label.localeCompare(b.label);

const toLanes = (members: PartRelationshipNode[]): PartRelationshipNode[][] => {
  const sorted = [...members].sort(byTypeThenLabel);
  return Array.from({ length: Math.ceil(sorted.length / MAX_ROWS_PER_LANE) }, (_, lane) =>
    sorted.slice(lane * MAX_ROWS_PER_LANE, (lane + 1) * MAX_ROWS_PER_LANE)
  );
};

const positionLane = (lane: PartRelationshipNode[], column: number, x: number): PositionedNode[] =>
  lane.map((node, index) => ({ node, column, x, y: (index - (lane.length - 1) / 2) * ROW_HEIGHT }));

export const layoutRelationshipNodes = (nodes: PartRelationshipNode[], seedNodeId: string): PositionedNode[] => {
  const columns = new Map<number, PartRelationshipNode[]>();
  for (const node of nodes) {
    const column = columnOf(node, seedNodeId);
    columns.set(column, [...(columns.get(column) ?? []), node]);
  }
  let x = 0;
  return [...columns.keys()]
    .sort((a, b) => a - b)
    .flatMap(column =>
      toLanes(columns.get(column) ?? []).flatMap(lane => {
        const positioned = positionLane(lane, column, x);
        x += COLUMN_WIDTH;
        return positioned;
      })
    );
};

export const edgeHandles = (sourceColumn: number, targetColumn: number) => {
  if (sourceColumn < targetColumn) return { sourceHandle: 'right-source', targetHandle: 'left-target' };
  if (sourceColumn > targetColumn) return { sourceHandle: 'left-source', targetHandle: 'right-target' };
  return { sourceHandle: 'left-source', targetHandle: 'left-target' };
};
